import { useEffect, useRef, useState } from "react";
import { CirclePlus, Search, Shuffle, X } from "lucide-react";
import { CreateListItemPayload, ItemSearchResult } from "modules/lists/types/lists";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { Payload } from "modules/cart/types/cart";


import { searchItem } from "modules/lists/services/listService";
import { getVisitorId } from "utils/analytics";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

const formatPrice = (price: number): string =>
    `$${price.toLocaleString("es-CL")}`;

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface ResultRowProps {
    product: ItemSearchResult;
    onAdd: () => void;
    isHighlighted?: boolean;
}

/** Single product result row — click anywhere to add immediately */
const ResultRow: React.FC<ResultRowProps> = ({ product, onAdd, isHighlighted }) => (
    <button
        type="button"
        onClick={onAdd}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted active:bg-muted/70${
            isHighlighted ? " bg-orange-50" : ""
        }`}
    >
        {/* Thumbnail */}
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-muted flex items-center justify-center">
            {product.imageUrl ? (
                <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="h-full w-full object-contain mix-blend-multiply"
                />
            ) : (
                <span className="text-xs text-muted-foreground">—</span>
            )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-snug">{product.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-xs text-muted-foreground">{product.brand}</span>
                {(product.total != null || product.unit) && (
                    <span className="text-xs text-muted-foreground">
                        · {[product.total, product.unit].filter(Boolean).join(" ")}
                    </span>
                )}
            </div>
        </div>

        {/* Price */}
        <span className="shrink-0 text-sm font-semibold tabular-nums">
            {product.price !== null ? formatPrice(product.price) : "—"}
        </span>
    </button>
);

interface SinPreferenciaRowProps {
    onAdd: () => void;
    isHighlighted?: boolean;
}

/** "Sin preferencia" row — click to add without linking to a specific product */
const SinPreferenciaRow: React.FC<SinPreferenciaRowProps> = ({ onAdd, isHighlighted }) => (
    <button
        type="button"
        onClick={onAdd}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border-t mt-1 hover:bg-muted active:bg-muted/70${
            isHighlighted ? " bg-orange-50" : ""
        }`}
    >
        <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-md bg-muted">
            <Shuffle className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Sin preferencia</p>
            <p className="text-xs text-muted-foreground">El optimizador elige el mejor precio</p>
        </div>
        <span className="shrink-0 text-sm text-muted-foreground">—</span>
    </button>
);

interface BuscarMasRowProps {
    onClick: () => void;
}

/** "Buscar más" row — opens the full product search picker */
const BuscarMasRow: React.FC<BuscarMasRowProps> = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors border-t mt-1 hover:bg-muted active:bg-muted/70"
    >
        <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-md bg-muted">
            <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Buscar más</p>
            <p className="text-xs text-muted-foreground">Explorar el catálogo completo</p>
        </div>
    </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface InlineProductSearchProps {
    /** Called immediately when the user clicks a product row */
    onAdd: (item: CreateListItemPayload) => Promise<void>;
    /**
     * When true, hides the "Sin preferencia" option from the results panel.
     * Use this in view-mode (quote-only) contexts where free-text items are not allowed.
     */
    hideFreeOption?: boolean;
    /** Custom placeholder text for the search input. Defaults to "¿Qué necesitas?" */
    placeholder?: string;
}

/**
 * Inline product search for the list detail panel.
 * - Debounces the query 300 ms then calls the backend parser + catalog search
 * - Shows up to 5 real product results + "Sin preferencia" option (unless hideFreeOption)
 * - Clicking any row immediately adds the item (no intermediate confirmation)
 * - Passes parsed quantity/unit from the backend into the item payload
 */
const InlineProductSearch: React.FC<InlineProductSearchProps> = ({ onAdd, hideFreeOption = false, placeholder = "¿Qué necesitas?" }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<ItemSearchResult[]>([]);
    const [attributionToken, setAtributionToken] = useState<string>("");
    const [parsedQuantity, setParsedQuantity] = useState<number>(1);
    const [parsedUnit, setParsedUnit] = useState<string | null>(null);
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    // Debounced search — fires real API call after 700 ms of inactivity, cancels previous in-flight request
    useEffect(() => {
        if (!query.trim() || query.trim().length < 2) {
            setResults([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            // Cancel any previous in-flight request
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            const { signal } = abortRef.current;

            setLoading(true);
            try {
                const response = await searchItem(query.trim(), signal, hideFreeOption);
                setResults(response.results);
                setAtributionToken(response.attribution_token);
                setParsedQuantity(response.parsedQuantity);
                setParsedUnit(response.parsedUnit);
                setCategoryId(response.categoryId);
            } catch (err) {
                if (err instanceof Error && err.name === "AbortError") return;
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 700);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            abortRef.current?.abort();
        };
    }, [query]);

    // Reset highlight whenever the query changes (results are stale during debounce)
    useEffect(() => {
        setHighlightedIndex(null);
    }, [query]);

    const hasResults = results.length > 0 || query.trim().length > 0;

    /** Clears the search input and dismisses results */
    const handleClear = () => {
        setQuery("");
        setResults([]);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        abortRef.current?.abort();
    };

    const handleAddProduct = async (product: ItemSearchResult) => {
        try {
            if (!window.dataLayer) window.dataLayer = [];
            window.dataLayer.push({
                event: "add_to_cart",
                product_id: product.s_id,
                quantity: 1,
                visitor_id: getVisitorId(),
                attribution_token: attributionToken || "",
            });
            await onAdd({
                rawDescription: query.trim(),
                catalogProductId: product.id,
                catalogProductSku: product.sku ?? undefined,
                categoryId: product.categoryId,
                quantity: parsedQuantity,
                unit: parsedUnit ?? product.unit ?? null,
                notes: null,
                name: product.name,
            });
            setQuery("");
            setResults([]);
        } catch {
            // Non-critical; error handling delegated to parent
        }
    };

    /**
     * Translates a cart Payload (from ProductSearchPicker) into a CreateListItemPayload
     * and delegates to onAdd, then closes the picker.
     *
     * Uses `payload.hashedSku ?? payload.sku` for catalogProductSku:
     * - hashedSku is the canonical catalog SKU (hashed_sku from ProductData) — what the backend expects.
     * - payload.sku is the store-level SKU — used only as a fallback when hashed_sku is absent.
     */
    const handlePickerProductSelected = async (payload: Payload) => {
        try {
            if (!window.dataLayer) window.dataLayer = [];
            window.dataLayer.push({
                event: "add_to_cart",
                product_id: payload.s_id,
                quantity: 1,
                visitor_id: getVisitorId(),
                attribution_token: attributionToken || "",
            });
            await onAdd({
                rawDescription: query.trim() || (payload.name ?? String(payload.id)),
                catalogProductId: payload.id,
                catalogProductSku: payload.hashedSku ?? payload.sku,
                categoryId: payload.category_id,
                quantity: payload.quantity,
                unit: null,
                notes: null,
                name: payload.name ?? undefined,
            });
            setQuery("");
            setResults([]);
            setPickerOpen(false);
        } catch {
            // Non-critical; error handling delegated to parent
        }
    };

    const handleAddSinPreferencia = async () => {
        try {
            await onAdd({
                rawDescription: query.trim(),
                catalogProductId: null,
                categoryId: categoryId,
                quantity: parsedQuantity,
                unit: parsedUnit ?? null,
                notes: null,
            });
            setQuery("");
            setResults([]);
        } catch {
            // Non-critical; error handling delegated to parent
        }
    };

    return (
        <div className="relative w-full min-w-0">
            {/* Search input */}
            <InputGroup className="w-full rounded-full">
                <InputGroupInput
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") { handleClear(); return; }

                        if (e.key === "ArrowDown") {
                            if (!hasResults || loading) return;
                            e.preventDefault();
                            // results[0..n-1] + sin-preferencia at index n
                            const max = results.length;
                            setHighlightedIndex((prev) =>
                                prev === null ? 0 : prev >= max ? 0 : prev + 1
                            );
                            return;
                        }

                        if (e.key === "ArrowUp") {
                            if (!hasResults || loading) return;
                            e.preventDefault();
                            const max = results.length;
                            setHighlightedIndex((prev) =>
                                prev === null ? max : prev === 0 ? null : prev - 1
                            );
                            return;
                        }

                        if (e.key === "Enter") {
                            if (!query.trim()) return;
                            e.preventDefault();

                            // Cancel any pending search so Enter is immediate
                            if (debounceRef.current) clearTimeout(debounceRef.current);
                            abortRef.current?.abort();

                            if (highlightedIndex !== null && highlightedIndex < results.length) {
                                handleAddProduct(results[highlightedIndex]);
                            } else {
                                handleAddSinPreferencia();
                            }
                        }
                    }}
                />
                <InputGroupAddon align="inline-end">
                    {query.trim() ? (
                        <InputGroupButton onClick={handleClear} size="icon-xs" aria-label="Limpiar búsqueda">
                            <X className="text-muted-foreground" />
                        </InputGroupButton>
                    ) : (
                        <InputGroupButton onClick={() => {}} size="icon-xs">
                            <CirclePlus className="text-secondary" />
                        </InputGroupButton>
                    )}
                </InputGroupAddon>
            </InputGroup>

            {/* Results panel */}
            {hasResults && (
                <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border bg-card shadow-md overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/40 min-w-0">
                        <span className="text-xs font-semibold text-muted-foreground shrink-0">
                            Coincidencias
                        </span>
                        <span className="truncate text-xs text-muted-foreground text-right">
                            "{query}"
                        </span>
                    </div>

                    {/* Product rows */}
                    <div className="flex flex-col px-1 py-1">
                        {loading ? (
                            <p className="py-4 text-center text-xs text-muted-foreground">Buscando...</p>
                        ) : results.length === 0 ? (
                            <p className="py-4 text-center text-xs text-muted-foreground">
                                Sin resultados para "{query}"
                            </p>
                        ) : (
                            results.map((product, index) => (
                                <ResultRow
                                    key={product.id}
                                    product={product}
                                    onAdd={() => handleAddProduct(product)}
                                    isHighlighted={highlightedIndex === index}
                                />
                            ))
                        )}

                        {/* Sin preferencia — hidden when hideFreeOption is set */}
                        {!loading && results.length > 0 && !hideFreeOption && (
                            <SinPreferenciaRow
                                onAdd={handleAddSinPreferencia}
                                isHighlighted={highlightedIndex === results.length}
                            />
                        )}

                        {/* Buscar más — opens full catalog picker when query is long enough */}
                        {!loading && query.trim().length >= 2 && (
                            <BuscarMasRow onClick={() => setPickerOpen(true)} />
                        )}
                    </div>
                </div>
            )}

            <ProductSearchPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                initialQuery={query}
                onProductSelected={handlePickerProductSelected}
            />
        </div>
    );
};

export { InlineProductSearch };
